import { Form, FormInstance, Spin } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { FormItem } from 'components/common/form-item';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useGetOrganizationUnitDetailQuery, useUpdateOrganizationUnitMutation } from 'services';
import { UserCompactDto } from 'types';
import UpdateStaffList, { UpdatedUserCompactDto } from './UpdateStaffList';
import { useLazyOrganizationUnitUsers } from 'hooks';

export type OrganizationUnitUpdateStaffsFormProps = {
  onChangeLoading?: (value: boolean) => void;
  onChangeSuccess?: () => void;
  organizationUnitId: number;
};

export type OrganizationUnitUpdateStaffsFormRefProps = {
  form: FormInstance<OrganizationUnitUpdateStaffsFormType>;
  isLoading: boolean;
};
export type OrganizationUnitUpdateStaffsFormType = {
  users: UserCompactDto[];
};
const OrganizationUnitUpdateStaffsForm = forwardRef<
  OrganizationUnitUpdateStaffsFormRefProps,
  OrganizationUnitUpdateStaffsFormProps
>(({ organizationUnitId, onChangeLoading, onChangeSuccess }, ref) => {
  useImperativeHandle(ref, () => ({
    form: form,
    isLoading: isLoadingUpdate
  }));

  const { data: organizationUnitRes, isLoading } = useGetOrganizationUnitDetailQuery(organizationUnitId!, {
    skip: !organizationUnitId
  });

  const { fetchData, data, isLoading: isLoadingUsers, count, handleLoadMore } = useLazyOrganizationUnitUsers();

  const organizationUnit = organizationUnitRes?.data;

  const [form] = Form.useForm<OrganizationUnitUpdateStaffsFormType>();
  const [onUpdate, { isLoading: isLoadingUpdate }] = useUpdateOrganizationUnitMutation();
  const onFinish = (values: OrganizationUnitUpdateStaffsFormType) => {
    onUpdate({
      organizationUnitId,
      userIds: values.users.map((o) => o.userId)
    })
      .unwrap()
      .then(() => {
        onChangeSuccess?.();
      });
  };
  useEffect(() => {
    if (data && organizationUnitId) {
      form.setFieldsValue({
        users: data
      });
    }
  }, [data, organizationUnitId]);

  useEffect(() => {
    if (onChangeLoading) {
      onChangeLoading(isLoadingUpdate);
    }
  }, [onChangeLoading, isLoadingUpdate]);

  useEffect(() => {
    if (!organizationUnitId) return;
    fetchData({ organizationUnitId });
  }, [organizationUnitId]);

  const staffs: UpdatedUserCompactDto[] = Form.useWatch('users', form);
  const handleRemove = (record: UpdatedUserCompactDto) => {
    form.setFieldValue(
      'users',
      staffs.filter((o) => o.userId !== record.userId)
    );
  };

  const handleAdd = (record: DefaultOptionType & UpdatedUserCompactDto) => {
    form.setFieldValue('users', [
      {
        userId: record.userId,
        fullName: record.fullName,
        shortName: record.shortName,
        avatar: record.avatar,
        isAdded: true
      },
      ...staffs.filter((o) => o.userId !== record.userId)
    ]);
  };
  return (
    <Form
      scrollToFirstError={{ behavior: 'smooth', block: 'start' }}
      labelAlign='right'
      labelCol={{
        flex: '150px'
      }}
      requiredMark={false}
      form={form}
      name='organizationUnitUserForm'
      onFinish={onFinish}
      layout='horizontal'
    >
      <FormItem<OrganizationUnitUpdateStaffsFormType> name='users' noStyle />
      <Spin spinning={!organizationUnit || isLoading || isLoadingUsers}>
        <UpdateStaffList
          users={staffs}
          totalUsers={count}
          onAdd={handleAdd}
          onRemove={handleRemove}
          onLoadMore={handleLoadMore}
        />
      </Spin>
    </Form>
  );
});
export default OrganizationUnitUpdateStaffsForm;
